/**
 * Event wire contract.
 *
 * Hand-authored TypeScript — the single source of truth, imported directly by
 * the Worker (`apps/edge`) and the frontend (`apps/web`). No code generation.
 * Adding an event type means adding it here and producing it from the Durable
 * Object hub. See docs/EVENT_ARCHITECTURE.md.
 */

import type { Link, Pop } from './topology.js';

export type ChannelName =
  | 'tiw.topology.update'
  | 'tiw.flow.tick'
  | 'tiw.incident.open'
  | 'tiw.incident.close'
  | 'tiw.bgp.withdraw'
  | 'tiw.dns.spike';

export type IncidentSeverity = 'info' | 'warn' | 'critical';

/** Fields shared by every event envelope. */
export interface EnvelopeBase {
  /** ISO 8601 UTC. */
  readonly ts: string;
  /** Per-channel monotonic id, `<unix_ms>-<seq>`. */
  readonly id: string;
  /** Producing component, e.g. `edge`. */
  readonly source: string;
}

export interface TopologyUpdatePayload {
  readonly added_pops: readonly Pop[];
  readonly removed_pops: readonly string[];
  readonly changed_links: readonly Link[];
}
export interface TopologyUpdateEvent extends EnvelopeBase {
  readonly channel: 'tiw.topology.update';
  readonly payload: TopologyUpdatePayload;
}

export interface FlowSample {
  readonly link_id: string;
  readonly bps_forward: number;
  readonly bps_reverse: number;
  readonly loss_pct: number;
}
export interface FlowTickPayload {
  readonly samples: readonly FlowSample[];
}
export interface FlowTickEvent extends EnvelopeBase {
  readonly channel: 'tiw.flow.tick';
  readonly payload: FlowTickPayload;
}

export interface IncidentScope {
  readonly pop_ids: readonly string[];
  readonly asns: readonly number[];
}
export interface IncidentOpenPayload {
  readonly incident_id: string;
  readonly severity: IncidentSeverity;
  readonly scope: IncidentScope;
  readonly title: string;
  readonly description?: string | null;
}
export interface IncidentOpenEvent extends EnvelopeBase {
  readonly channel: 'tiw.incident.open';
  readonly payload: IncidentOpenPayload;
}

export interface IncidentClosePayload {
  readonly incident_id: string;
  readonly resolution: string;
}
export interface IncidentCloseEvent extends EnvelopeBase {
  readonly channel: 'tiw.incident.close';
  readonly payload: IncidentClosePayload;
}

export interface BgpWithdrawPayload {
  readonly prefix: string;
  readonly origin_asn: number;
  readonly observed_by: readonly number[];
  readonly impacted_pops: readonly string[];
}
export interface BgpWithdrawEvent extends EnvelopeBase {
  readonly channel: 'tiw.bgp.withdraw';
  readonly payload: BgpWithdrawPayload;
}

export interface DnsQuery {
  readonly name: string;
  readonly count: number;
}
export interface DnsSpikePayload {
  readonly resolver: string;
  readonly qps: number;
  readonly baseline_qps: number;
  readonly top_queries: readonly DnsQuery[];
}
export interface DnsSpikeEvent extends EnvelopeBase {
  readonly channel: 'tiw.dns.spike';
  readonly payload: DnsSpikePayload;
}

/** Discriminated union over every event the backend can emit. */
export type EventEnvelope =
  | TopologyUpdateEvent
  | FlowTickEvent
  | IncidentOpenEvent
  | IncidentCloseEvent
  | BgpWithdrawEvent
  | DnsSpikeEvent;

/**
 * Map from channel name to its concrete event type. Consumed by the typed Bus
 * in @tiw/event-bus so `on('tiw.flow.tick', ...)` is fully typed.
 */
export type EventMap = {
  readonly 'tiw.topology.update': TopologyUpdateEvent;
  readonly 'tiw.flow.tick': FlowTickEvent;
  readonly 'tiw.incident.open': IncidentOpenEvent;
  readonly 'tiw.incident.close': IncidentCloseEvent;
  readonly 'tiw.bgp.withdraw': BgpWithdrawEvent;
  readonly 'tiw.dns.spike': DnsSpikeEvent;
};

/** Runtime catalog for iteration / subscription. Mirrors the union members. */
export const CHANNELS = {
  'tiw.topology.update': 'tiw.topology.update',
  'tiw.flow.tick': 'tiw.flow.tick',
  'tiw.incident.open': 'tiw.incident.open',
  'tiw.incident.close': 'tiw.incident.close',
  'tiw.bgp.withdraw': 'tiw.bgp.withdraw',
  'tiw.dns.spike': 'tiw.dns.spike',
} as const satisfies Readonly<Record<ChannelName, ChannelName>>;
